/**
 * Tiny dense linear algebra for the Levenberg-Marquardt step in the NN-Newton foil optimizer
 * (see foil-design-optimizer-DESIGN.md §8). No external deps. Matrices are row-major number[][];
 * vectors are number[]. All functions are pure (inputs never mutated) and assume well-formed,
 * non-jagged inputs. Sized for tiny systems (a handful of design params), so clarity over speed.
 */

/** Transpose: A is m×n → returns n×m. */
export function matT(A: number[][]): number[][] {
  const m = A.length, n = m ? A[0]!.length : 0;
  const T: number[][] = Array.from({ length: n }, () => new Array<number>(m));
  for (let i = 0; i < m; i++) {
    const Ai = A[i]!;
    for (let j = 0; j < n; j++) T[j]![i] = Ai[j]!;
  }
  return T;
}

/** Matrix product: A (m×k) · B (k×n) → m×n. */
export function matMul(A: number[][], B: number[][]): number[][] {
  const m = A.length, k = m ? A[0]!.length : 0, n = k ? B[0]!.length : 0;
  const C: number[][] = Array.from({ length: m }, () => new Array<number>(n).fill(0));
  for (let i = 0; i < m; i++) {
    const Ai = A[i]!, Ci = C[i]!;
    for (let p = 0; p < k; p++) {
      const a = Ai[p]!, Bp = B[p]!;
      if (a === 0) continue;
      for (let j = 0; j < n; j++) Ci[j]! += a * Bp[j]!;
    }
  }
  return C;
}

/** Matrix·vector: A (m×n) · x (n) → m. */
export function matVec(A: number[][], x: number[]): number[] {
  const m = A.length, n = m ? A[0]!.length : 0;
  const y = new Array<number>(m).fill(0);
  for (let i = 0; i < m; i++) {
    const Ai = A[i]!;
    let s = 0;
    for (let j = 0; j < n; j++) s += Ai[j]! * x[j]!;
    y[i] = s;
  }
  return y;
}

/** Return a copy of square A with lambda added to its diagonal (A + lambda·I). */
export function addDiag(A: number[][], lambda: number): number[][] {
  const n = A.length;
  const R: number[][] = A.map((row) => row.slice());
  for (let i = 0; i < n; i++) R[i]![i]! += lambda;
  return R;
}

/**
 * Solve A·x = b for symmetric positive-definite A via Cholesky (A = L·Lᵀ).
 * Falls back to LU with partial pivoting if A is not numerically SPD (non-positive pivot).
 * Does not mutate A or b. Throws if the system is singular.
 */
export function solveSPD(A: number[][], b: number[]): number[] {
  const n = A.length;
  // Cholesky: L lower-triangular, L·Lᵀ = A.
  const L: number[][] = Array.from({ length: n }, () => new Array<number>(n).fill(0));
  let spd = true;
  for (let i = 0; i < n && spd; i++) {
    const Ai = A[i]!, Li = L[i]!;
    for (let j = 0; j <= i; j++) {
      const Lj = L[j]!;
      let s = Ai[j]!;
      for (let k = 0; k < j; k++) s -= Li[k]! * Lj[k]!;
      if (i === j) {
        if (s <= 0) { spd = false; break; }
        Li[j] = Math.sqrt(s);
      } else {
        Li[j] = s / Lj[j]!;
      }
    }
  }
  if (spd) {
    // Forward solve L·y = b, then back solve Lᵀ·x = y.
    const y = new Array<number>(n).fill(0);
    for (let i = 0; i < n; i++) {
      const Li = L[i]!;
      let s = b[i]!;
      for (let k = 0; k < i; k++) s -= Li[k]! * y[k]!;
      y[i] = s / Li[i]!;
    }
    const x = new Array<number>(n).fill(0);
    for (let i = n - 1; i >= 0; i--) {
      let s = y[i]!;
      for (let k = i + 1; k < n; k++) s -= L[k]![i]! * x[k]!;
      x[i] = s / L[i]![i]!;
    }
    return x;
  }
  return solveLU(A, b);
}

/** Solve A·x = b by LU decomposition with partial pivoting. Pure; throws if singular. */
export function solveLU(A: number[][], b: number[]): number[] {
  const n = A.length;
  const M: number[][] = A.map((row) => row.slice());
  const x = b.slice();
  for (let col = 0; col < n; col++) {
    // Partial pivot: largest magnitude in this column at/below the diagonal.
    let piv = col, best = Math.abs(M[col]![col]!);
    for (let r = col + 1; r < n; r++) {
      const v = Math.abs(M[r]![col]!);
      if (v > best) { best = v; piv = r; }
    }
    if (best === 0) throw new Error('solveLU: singular matrix');
    if (piv !== col) {
      const tmp = M[piv]!; M[piv] = M[col]!; M[col] = tmp;
      const tb = x[piv]!; x[piv] = x[col]!; x[col] = tb;
    }
    const Mc = M[col]!, d = Mc[col]!;
    for (let r = col + 1; r < n; r++) {
      const Mr = M[r]!;
      const f = Mr[col]! / d;
      if (f === 0) continue;
      for (let c = col; c < n; c++) Mr[c]! -= f * Mc[c]!;
      x[r]! -= f * x[col]!;
    }
  }
  // Back substitution.
  for (let i = n - 1; i >= 0; i--) {
    const Mi = M[i]!;
    let s = x[i]!;
    for (let k = i + 1; k < n; k++) s -= Mi[k]! * x[k]!;
    x[i] = s / Mi[i]!;
  }
  return x;
}
